import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { BroadcastDetailsModalProps, TabId } from './types';
import { aggregateApps, aggregateCauses, filterPages, fmtDateTime, fmtDec, fmtDuration, fmtInt, normalizeItems } from './utils';
import { ChartsTab } from './ChartsTab';
import { ExecutionsTab } from './ExecutionsTab';
import { DistributionTab } from './DistributionTab';
import s from './BroadcastDetailsModal.module.css';

const STATUS: Record<string, { label: string; bg: string; fg: string; bd: string }> = {
  partial: { label: 'Enviado parcialmente', bg: '#fef3c7', fg: '#92400e', bd: '#fcd34d' },
  completed: { label: 'Enviado', bg: '#dcfce7', fg: '#166534', bd: '#86efac' },
  sent: { label: 'Enviado', bg: '#dcfce7', fg: '#166534', bd: '#86efac' },
  failed: { label: 'Falhou', bg: '#fee2e2', fg: '#991b1b', bd: '#fca5a5' },
  processing: { label: 'Em andamento', bg: '#dbeafe', fg: '#1e40af', bd: '#93c5fd' },
  scheduled: { label: 'Agendado', bg: '#f3f4f6', fg: '#374151', bd: '#d1d5db' },
};

export function BroadcastDetailsModal({ open, onClose, group, items, loading, appNames, defaultTab = 'charts' }: BroadcastDetailsModalProps) {
  const [tab, setTab] = useState<TabId>(defaultTab);
  const [q, setQ] = useState('');
  const [onlyErrors, setOnlyErrors] = useState(false);
  const [cause, setCause] = useState<string | null>(null);
  const [openApps, setOpenApps] = useState<Set<number>>(new Set());

  // reset ao trocar de grupo; filtros persistem entre abas
  useEffect(() => {
    setTab(defaultTab); setQ(''); setOnlyErrors(false); setCause(null); setOpenApps(new Set());
  }, [group?.group_broadcast_id, defaultTab]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const appName = useCallback((id: number) => appNames?.[id] ?? `App ${id}`, [appNames]);
  const pages = useMemo(() => normalizeItems(items ?? []), [items]);
  const filtered = useMemo(() => filterPages(pages, q, onlyErrors, appName), [pages, q, onlyErrors, appName]);
  const causes = useMemo(() => aggregateCauses(filtered), [filtered]);
  const apps = useMemo(() => aggregateApps(filtered), [filtered]);

  const toggleApp = (app: number) => setOpenApps((prev) => { const n = new Set(prev); if (n.has(app)) n.delete(app); else n.add(app); return n; });
  const openAppInDistribution = (app: number) => { setOpenApps(new Set([app])); setTab('distribution'); };

  if (!open) return null;

  const st = STATUS[group?.status ?? ''] ?? STATUS.scheduled;
  const langs = group?.target_languages ?? (group?.target_language ? [group.target_language] : []);
  const dur = group?.started_at && group?.completed_at ? (Date.parse(group.completed_at) - Date.parse(group.started_at)) / 1000 : null;
  const isFiltered = filtered.length !== pages.length;

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: 'charts', label: 'Gráficos' },
    { id: 'executions', label: 'Execuções', count: filtered.length },
    { id: 'distribution', label: 'Distribuição', count: apps.length },
  ];

  return createPortal(
    <div className={s.overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={s.dialog} role="dialog" aria-modal="true" aria-label={group?.title ?? 'Detalhes do envio'}>
        <header className={s.header}>
          {group ? (
            <>
              <h2 className={s.title}>{group.title}</h2>
              <span className={s.status} style={{ background: st.bg, color: st.fg, borderColor: st.bd }}>{st.label}</span>
              <div className={s.meta}>
                <span>#{group.group_broadcast_id}</span>
                <span>{group.message_tag}</span>
                {!!group.ai_translate && langs.length > 0 && (
                  <span className={s.lang} tabIndex={0}>
                    <b>IA</b>{langs.length} {langs.length === 1 ? 'idioma' : 'idiomas'}
                    <span className={s.langPop} role="tooltip">
                      <small>TRADUZIDO PARA</small>
                      {langs.map((l) => <span key={l}>{l}</span>)}
                    </span>
                  </span>
                )}
                <span>Agendado {fmtDateTime(group.scheduled_at)} BRT</span>
                <span>por {group.user_name}</span>
              </div>
            </>
          ) : (
            <div className={s.skLine} style={{ width: 420 }} />
          )}
          <button type="button" className={s.close} onClick={onClose} aria-label="Fechar">×</button>
        </header>

        <div className={s.kpis}>
          {loading || !group ? (
            Array.from({ length: 7 }).map((_, i) => <div key={i} className={s.kpi}><div className={s.skLine} style={{ width: 60, height: 10 }} /><div className={s.skLine} style={{ width: 90, height: 18 }} /></div>)
          ) : (
            <>
              <Kpi label="Páginas" value={fmtInt(group.total_pages)} sub={<>{fmtInt(group.pages_sent)} ok · <span className={s.red}>{fmtInt(group.pages_failed)} falharam</span></>} />
              <Kpi label="Leads" value={fmtInt(group.total_leads)} />
              <Kpi label="Enviados" value={fmtInt(group.total_sent)} color="#2563eb" />
              <Kpi label="Entregues" value={fmtInt(group.total_delivered)} color="#4f46e5" />
              <Kpi label="Falhas" value={fmtInt(group.total_failed)} color="#dc2626" />
              <Kpi label="Sucesso" value={`${fmtDec(group.success_rate)}%`} color="#059669" />
              <Kpi label="Duração" value={dur != null ? fmtDuration(dur) : '—'} sub={dur != null ? `${fmtDateTime(group.started_at).slice(-5)} → ${fmtDateTime(group.completed_at).slice(-5)} BRT` : undefined} />
            </>
          )}
        </div>

        <div className={s.tabsBar}>
          {tabs.map((t) => (
            <button key={t.id} type="button" className={`${s.tab} ${tab === t.id ? s.tabOn : ''}`} onClick={() => setTab(t.id)}>
              {t.label}{t.count != null && <span className={s.tabCount}>{t.count}</span>}
            </button>
          ))}
          <div className={s.filters}>
            {isFiltered && <span className={s.muted}>{filtered.length} de {pages.length} páginas</span>}
            <button type="button" className={s.toggleWrap} onClick={() => setOnlyErrors((v) => !v)} aria-pressed={onlyErrors}>
              <span className={`${s.toggle} ${onlyErrors ? s.toggleOn : ''}`}><span /></span>Apenas com erros
            </button>
            <div className={s.search}>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar página, app ou ID…" />
              {q && <button type="button" onClick={() => setQ('')} aria-label="Limpar busca">×</button>}
            </div>
          </div>
        </div>

        <div className={s.body}>
          {loading ? (
            <div className={s.skBody}><div className={s.skBlock} style={{ flex: 1.4 }} /><div className={s.skRow}><div className={s.skBlock} /><div className={s.skBlock} /></div></div>
          ) : tab === 'charts' ? (
            <ChartsTab pages={filtered} allPages={pages} causes={causes} apps={apps} cause={cause} onCause={setCause} onOpenApp={openAppInDistribution} appName={appName} />
          ) : tab === 'executions' ? (
            <ExecutionsTab pages={filtered} cause={cause} appName={appName} />
          ) : (
            <DistributionTab apps={apps} causes={causes} cause={cause} onCause={setCause} openApps={openApps} onToggleApp={toggleApp} appName={appName} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Kpi({ label, value, sub, color }: { label: string; value: string; sub?: ReactNode; color?: string }) {
  return (
    <div className={s.kpi}>
      <span className={s.kpiLabel}>{label}</span>
      <span className={s.kpiValue} style={{ color }}>{value}{sub && <small>{sub}</small>}</span>
    </div>
  );
}
